import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import { Badge, Callout, Tooltip, Typography } from 'seagull';
import { DollarCircle } from 'seagull/icons';
import { useResponsive } from '~/client-flow/contexts';
import { useIoContext } from '~/contexts';
import { useServicesBillingDetails } from '~/hooks/reactQuery/queries/useServicesBillingDetails';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { relationshipModel } from '~/models';
import { utils } from '~/utils';
import { BillingGroupTypes, summarize } from '../genServicesSummary';
import styles from './BillingSchedule.module.scss';
import { getBillingGroupTitle, processBillingGroups } from './BillingSchedule.utils';
import BillingStageRow from './BillingStageRow';
function renderServiceRow(param) {
    let { variablePrice, name, price, minPrice, isRange, unit, prorated, hasBilledUpfront, groupType, serviceId, servicesBillingDetails, START_BILLING_AT } = param;
    var _servicesBillingDetails_serviceId;
    const priorPeriods = ((_servicesBillingDetails_serviceId = servicesBillingDetails[serviceId]) === null || _servicesBillingDetails_serviceId === void 0 ? void 0 : _servicesBillingDetails_serviceId.periods) || 0;
    const getServiceHeaderText = ()=>{
        if (variablePrice && !isRange) {
            return "".concat(name, " (").concat(utils.formatDollarAmount(price), "/").concat(unit || 'hour', ")");
        }
        if (isRange) {
            return "".concat(name, " (").concat(minPrice != null ? "".concat(utils.formatDollarAmount(minPrice), " - ") : 'up to ').concat(utils.formatDollarAmount(price), ")");
        }
        return "".concat(name, " ").concat(priorPeriods > 0 && START_BILLING_AT && groupType == BillingGroupTypes.Today ? "(".concat(priorPeriods, " prior periods)") : '');
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                testId: "billing-schedule-service-".concat(name, "-").concat(groupType, "-").concat(variablePrice, "-").concat(priorPeriods),
                children: getServiceHeaderText()
            }),
            prorated && (groupType === BillingGroupTypes.Today || !hasBilledUpfront) && /*#__PURE__*/ _jsx(Tooltip, {
                placement: "right",
                title: 'The final payment amount will be calculated based on the proposal approval date',
                children: /*#__PURE__*/ _jsx(Badge, {
                    rounded: true,
                    outlined: true,
                    skin: "warning",
                    className: styles.billingScheduleListItemBadge,
                    testId: "billing-schedule-service-prorated-badge",
                    children: "Prorated"
                })
            })
        ]
    });
}
const BillingSchedule = (param)=>{
    let { selectedOptionalServices, selectedBundleID, relationship } = param;
    const { isMobile } = useResponsive();
    const { getNow } = useIoContext();
    const { START_BILLING_AT = false, START_BILLING_AT_PHASE_2_INLINE = false } = useFeatureFlags();
    const startBillingAtEnabled = START_BILLING_AT || START_BILLING_AT_PHASE_2_INLINE;
    const effectiveDate = relationshipModel.effectiveDate(relationship);
    const services = useMemo(()=>relationshipModel.actualServices(relationship, selectedBundleID), [
        relationship,
        selectedBundleID
    ]);
    const { servicesBillingDetails } = useServicesBillingDetails({
        services,
        relationshipEffectiveDate: effectiveDate
    });
    const servicesWithPriorPeriods = useMemo(()=>{
        const map = {};
        services.forEach((service)=>{
            var _ref;
            var _servicesBillingDetails_serviceId;
            const serviceId = service.id;
            const priorPeriods = (_ref = (_servicesBillingDetails_serviceId = servicesBillingDetails[serviceId]) === null || _servicesBillingDetails_serviceId === void 0 ? void 0 : _servicesBillingDetails_serviceId.periods) !== null && _ref !== void 0 ? _ref : 0;
            map[serviceId] = priorPeriods > 0;
        });
        return map;
    }, [
        services,
        servicesBillingDetails
    ]);
    const billingSummary = useMemo(()=>services.reduce(summarize({
            selectedOptionalServices: selectedOptionalServices || [],
            effectiveDate,
            now: getNow(),
            START_BILLING_AT: startBillingAtEnabled,
            servicesWithPriorPeriods
        }), {}), [
        services,
        selectedOptionalServices,
        effectiveDate,
        startBillingAtEnabled,
        servicesWithPriorPeriods
    ]);
    const serviceNameToIdMap = useMemo(()=>{
        const map = {};
        services.forEach((service)=>{
            map[service.name] = service.id;
        });
        return map;
    }, [
        services
    ]);
    const getGroups = ()=>{
        return processBillingGroups(billingSummary, serviceNameToIdMap, servicesBillingDetails, startBillingAtEnabled, getNow(), effectiveDate || undefined);
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: cn(styles.sectionTitle, {
                    [styles.mobile]: isMobile
                }),
                children: [
                    /*#__PURE__*/ _jsx(DollarCircle, {}),
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles.sectionTitleContent,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "h6",
                                weight: "bold",
                                children: "Billing Schedule"
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph1",
                                weight: "light",
                                skin: "secondary",
                                children: "For automatically billed services"
                            })
                        ]
                    })
                ]
            }),
            getGroups().map((param)=>{
                let { group, groupType, variablePrice, startDate, isPaused } = param;
                return /*#__PURE__*/ _jsxs("div", {
                    className: styles.billingSchedule,
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            variant: "paragraph1",
                            weight: "bold",
                            showWhiteSpaces: true,
                            breakWords: true,
                            className: cn(styles.coverLetter, {
                                [styles.mobile]: isMobile
                            }),
                            testId: "billing-schedule-group-title-".concat(groupType, "-").concat(variablePrice).concat(startDate ? "-".concat(startDate.toISOString().split('T')[0]) : ''),
                            children: [
                                getBillingGroupTitle(groupType, startDate, relationship, isPaused),
                                !isPaused && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        ' - ',
                                        !variablePrice ? utils.formatDollarAmount(group.totalAmount) : 'variable'
                                    ]
                                })
                            ]
                        }),
                        groupType === BillingGroupTypes.Today && group.hasBilledUpfront && /*#__PURE__*/ _jsx(Callout, {
                            intent: "info",
                            className: styles.billingScheduleListItem,
                            children: "First invoice contains recurring services in an active billing period."
                        }),
                        group.services.map((service, index)=>/*#__PURE__*/ _jsx("ul", {
                                className: cn(styles.listItem, styles.billingScheduleListItem),
                                "data-testid": "billing-schedule-service",
                                children: service.billingStages ? /*#__PURE__*/ _jsx(BillingStageRow, {
                                    name: service.name,
                                    price: service.price,
                                    billingStages: service.billingStages
                                }) : renderServiceRow(_object_spread_props(_object_spread({}, service), {
                                    variablePrice,
                                    hasBilledUpfront: group.hasBilledUpfront,
                                    groupType,
                                    serviceId: serviceNameToIdMap[service.name] || '',
                                    servicesBillingDetails,
                                    START_BILLING_AT: startBillingAtEnabled
                                }))
                            }, index))
                    ]
                }, "".concat(groupType, "-").concat(variablePrice, "-").concat(startDate === null || startDate === void 0 ? void 0 : startDate.toISOString()));
            })
        ]
    });
};
export default BillingSchedule;
